"use client";

import { create } from "zustand";

interface CommandPaletteStoreState {
  isOpen: boolean;
  /**
   * How the palette was last opened. Recorded because whether anyone actually
   * presses the hotkey — versus clicking the magnifying glass — is the main
   * open risk on this feature, and the split is only knowable from here.
   */
  openedFrom: "hotkey" | "icon" | null;
  open: (source: "hotkey" | "icon") => void;
  close: () => void;
  toggle: (source: "hotkey" | "icon") => void;

  /**
   * A search the palette could not answer, waiting for the chat composer.
   *
   * Carried here rather than in a `?q=` param because the palette is reachable
   * from `/dashboard/chat` itself, and pushing a route you are already on does
   * not hand a new param to an already-mounted client tree: the composer stayed
   * empty and the query was lost with no error. Verified in the browser.
   */
  pendingChatQuery: string | null;
  setPendingChatQuery: (query: string) => void;
  /** Clears it once the composer has taken it, so a later visit starts empty. */
  consumePendingChatQuery: () => void;
}

/**
 * Open/closed state for the command palette.
 *
 * A store rather than local state because two unrelated components drive it:
 * the global hotkey hook and the sidebar's magnifying glass, which sit on
 * opposite sides of the tree. Not persisted — the palette always starts closed.
 *
 * @returns `{ isOpen, openedFrom, open, close, toggle }`.
 */
export const useCommandPaletteStore = create<CommandPaletteStoreState>((set, get) => ({
  isOpen: false,
  openedFrom: null,
  open: (openedFrom) => set({ isOpen: true, openedFrom }),
  close: () => set({ isOpen: false }),
  toggle: (source) => (get().isOpen ? set({ isOpen: false }) : set({ isOpen: true, openedFrom: source })),

  pendingChatQuery: null,
  setPendingChatQuery: (pendingChatQuery) => set({ pendingChatQuery }),
  consumePendingChatQuery: () => set({ pendingChatQuery: null }),
}));
